import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useCart } from './CartContext';
import { useApi } from './ApiContext';
import toast from 'react-hot-toast';
import './Product.css';

// --- HELPER COMPONENT: DescriptionToggle ---
const DescriptionToggle = ({ text, maxLength = 80 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const descriptionText = text || 'No description available.';
    const needsToggle = descriptionText.length > maxLength;

    if (!needsToggle) {
        return <p className="product-description">{descriptionText}</p>;
    }

    const displayTruncatedText = descriptionText.substring(0, maxLength).trim();
    const visibleText = isExpanded ? descriptionText : displayTruncatedText;

    return (
        <div className="description-container">
            <p className="product-description">
                {visibleText}
                {!isExpanded && '... '}
                <button
                    className="toggle-description-button inline-toggle" 
                    onClick={(e) => {
                        e.stopPropagation(); 
                        setIsExpanded(prev => !prev);
                    }}
                >
                    {isExpanded ? 'Show Less' : 'Show More'}
                </button>
            </p>
        </div>
    );
};

// ------------------------------------------

const Product = () => {
    const { API_BASE_URL } = useApi();
    const PRODUCTS_API = `${API_BASE_URL}/products`;

    const [products, setProducts] = useState([]);
    const [selectedDuration, setSelectedDuration] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // STATE UNTUK KAWAL INFRAME/FOCUS MODE
    const [focusedProduct, setFocusedProduct] = useState(null);

    const { isAuthenticated, loading: authLoading } = useAuth();
    const { addToCart, getRemainingStock } = useCart();
    
    const history = useHistory();
    const location = useLocation();

    // 1. Ambil data produk dari API
    useEffect(() => {
        const fetchProducts = async () => {
            if (!API_BASE_URL) return;
            try {
                const res = await fetch(PRODUCTS_API, { credentials: 'include' });
                if (!res.ok) throw new Error('Failed to fetch products');
                const data = await res.json();
                setProducts(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [API_BASE_URL]);

    // 2. EFFECT KEMAS KINI: Baca URL Parameter jenis terus (?Donate)
    useEffect(() => {
        if (products.length === 0) return;

        const rawParam = location.search.substring(1); 

        if (rawParam) {
            const productNameFromUrl = decodeURIComponent(rawParam).replace(/-/g, ' ');

            const foundProduct = products.find(
                p => p.name.trim().toLowerCase() === productNameFromUrl.trim().toLowerCase()
            );

            if (foundProduct) {
                setFocusedProduct(foundProduct);
            } else {
                setFocusedProduct(null);
            }
        } else {
            setFocusedProduct(null);
        }
    }, [location.search, products]);

    // 3. FUNGSI KEMAS KINI: Set URL terus letak nama produk tanpa "product="
    const handleOpenFocus = (product) => {
        const productSlug = encodeURIComponent(product.name.replace(/\s+/g, '-'));
        history.push({ search: `?${productSlug}` });
    };

    // 4. FUNGSI KEMAS KINI: Kosongkan URL bila inframe ditutup
    const handleCloseFocus = () => {
        history.push({ search: '' });
    };

    const handleDurationChange = (productId, duration) => {
        setSelectedDuration(prev => ({
            ...prev,
            [productId]: duration
        }));
    };

    const handleAddToCart = (product) => {
        const duration = selectedDuration[product.id];
        if (!duration) {
            toast.error('Please select a duration');
            return;
        }

        if (authLoading) return;
        if (!isAuthenticated) {
            history.push('/login');
            return;
        }

        const durationData = product.product_data[duration];
        const remainingStock = getRemainingStock(product.id, duration, durationData?.count || 0);

        if (!durationData || durationData.count < 1) {
            toast.error('Selected duration is out of stock');
            return;
        }

        if (remainingStock <= 0) {
            toast.error('Stock limit reached for this duration');
            return;
        }

        const productToAdd = {
            ...product,
            selected_duration: duration,
            price: durationData.price,
            stock: durationData.count,
        };

        addToCart(productToAdd);
        return true; // Pulangkan true jika berjaya ditambah ke troli
    };

    if (loading) return <p className="status-text">Loading...</p>;
    if (error) return <p className="status-text error">Error: {error}</p>;

    return (
        <div className="Product">
            <h1>Our Products</h1>

            <div className="product-list">
                {products.map((product) => {
                    const durations = Object.keys(product.product_data || {});
                    const selected = selectedDuration[product.id];
                    const selectedData = product.product_data?.[selected];
                    const remainingStock = selected && selectedData
                        ? getRemainingStock(product.id, selected, selectedData.count)
                        : 0;

                    return (
                        <div key={product.id} className="product-card">
                            {/* Klik gambar atau nama sahaja untuk buka focus view */}
                            <div className="image-wrapper" onClick={() => handleOpenFocus(product)} style={{ cursor: 'pointer' }}>
                                <img
                                    src={product.image_link || 'https://bit.ly/product_image_default'}
                                    alt={product.name}
                                    className="product-image"
                                />
                            </div>
                            <div className="product-details">
                                <h2 onClick={() => handleOpenFocus(product)} style={{ cursor: 'pointer' }}>{product.name}</h2>
                                <DescriptionToggle text={product.description} maxLength={100} />

                                <select
                                    onChange={(e) => handleDurationChange(product.id, e.target.value)}
                                    value={selectedDuration[product.id] || ''}
                                    className="duration-select"
                                >
                                    <option value="">Select Duration</option>
                                    {durations.map((duration) => {
                                        const data = product.product_data[duration];
                                        const remaining = getRemainingStock(product.id, duration, data.count);
                                        return (
                                            <option key={duration} value={duration} disabled={remaining <= 0}>
                                                {duration} ({remaining <= 0 ? 'Out of stock' : `${remaining} available`})
                                            </option>
                                        );
                                    })}
                                </select>

                                <div className="price-add">
                                    <p className="price">
                                        {(() => {
                                            if (selected && selectedData) {
                                                return `RM${parseFloat(selectedData.price || 0).toFixed(2)}`;
                                            }
                                            const prices = Object.values(product.product_data || {}).map(d => parseFloat(d.price)).filter(p => !isNaN(p));
                                            if (prices.length === 0) return 'RM0.00';
                                            const min = Math.min(...prices).toFixed(2);
                                            const max = Math.max(...prices).toFixed(2);
                                            return min === max ? `RM${min}` : `RM${min} - RM${max}`;
                                        })()}
                                    </p>

                                    <button
                                        className="add-to-cart"
                                        onClick={() => handleAddToCart(product)}
                                        disabled={!selected || remainingStock <= 0}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ============================================================================= */}
            {/* INFRAME FOCUS MODE OVERLAY CONTAINER */}
            {/* ============================================================================= */}
            {focusedProduct && (
                <div className="product-focus-overlay" onClick={handleCloseFocus}>
                    <div className="product-focus-inframe" onClick={(e) => e.stopPropagation()}>
                        <button className="close-focus-btn" onClick={handleCloseFocus}>&times;</button>
                        
                        <div className="focus-content-layout">
                            <div className="focus-image-section">
                                <img 
                                    src={focusedProduct.image_link || 'https://bit.ly/product_image_default'} 
                                    alt={focusedProduct.name} 
                                Dino/>
                            </div>
                            
                            <div className="focus-details-section">
                                <h2>{focusedProduct.name}</h2>
                                <p className="focus-full-description">{focusedProduct.description || 'No description available.'}</p>
                                
                                <div className="focus-action-box">
                                    <label>Choose Duration:</label>
                                    <select
                                        onChange={(e) => handleDurationChange(focusedProduct.id, e.target.value)}
                                        value={selectedDuration[focusedProduct.id] || ''}
                                        className="duration-select"
                                    >
                                        <option value="">Select Duration</option>
                                        {Object.keys(focusedProduct.product_data || {}).map((duration) => {
                                            const data = focusedProduct.product_data[duration];
                                            const remaining = getRemainingStock(focusedProduct.id, duration, data.count);
                                            return (
                                                <option key={duration} value={duration} disabled={remaining <= 0}>
                                                    {duration} ({remaining <= 0 ? 'Out of stock' : `${remaining} available`})
                                                </option>
                                            );
                                        })}
                                    </select>

                                    <div className="focus-price-row">
                                        <span className="focus-price">
                                            {(() => {
                                                const sel = selectedDuration[focusedProduct.id];
                                                const selData = focusedProduct.product_data?.[sel];
                                                if (sel && selData) return `RM${parseFloat(selData.price || 0).toFixed(2)}`;
                                                return 'Select variation above';
                                            })()}
                                        </span>
                                        
                                        <button 
                                            className="focus-add-btn"
                                            onClick={() => {
                                                const success = handleAddToCart(focusedProduct);
                                                if (success) handleCloseFocus(); // Tutup inframe automatik bila barang berjaya ditambah
                                            }}
                                            disabled={!selectedDuration[focusedProduct.id]}
                                        >
                                            Add To Cart
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Product;